import { useContentRouteHelper } from '../content/useContentRouteHelper';
import { Eva2Error, ErrorTypes } from '~/lib/errors/Eva2Error';
import { getCategoryLockStates } from '~/lib/category/lockstate';
import type { CategoryLockState } from '~/lib/category/lockstate';
import { getCategoryAncestors } from '~/lib/category/ancestors';

export type UseCategoryPage = {
  categoryManager: UseCategory;
  categoryLockState: Ref<CategoryLockState>;
  categoryMainMedia: UseCategoryMainMedia;
  ancestorsLockState: Ref<LockStates>;
  libraryBookId: string;
  isParentChildPage: ComputedRef<boolean>;
  ancestors: Ref<CategorySchema[]>;
  isExam: boolean;
  customer: UseCategoryCustomer;
  mPageMedias: Ref<Map<string, MediaSchema>>;
  disciplineId: ComputedRef<string>;
  chapter: ComputedRef<CategorySchema>;
  discipline: ComputedRef<CategorySchema>;
  pageContentType: CategoryPageContentTypes;
  pageContextType: CategoryPageContextTypes;
  release: () => void;
  init: () => void;
  load: () => Promise<void>;
  error?: Eva2Error;
  contents: UseContentsForUserType;
  currentContent: Ref<UserContent>;
  contentMemberId: ComputedRef<string>;
  section: Ref<Sections>;
  categoryTrialContent: UseCategoryTrialContent;
};

export const INJECT_CATEGORY_PAGE = Symbol(
  'useCategoryPage composable'
) as InjectionKey<UseCategoryPage>;

export const useCategoryPage = (
  pageContextType: CategoryPageContextTypes,
  pageContentType: CategoryPageContentTypes = CategoryPageContentTypes.Category
): UseCategoryPage => {
  const { $db } = useNuxtApp();

  const route = useRoute();

  const profile = useProfileStore();

  const context = useContextStore();

  const { mainContent, downloadedContents, all: allContents } = useContents();

  const ancestors = ref<CategorySchema[]>([]);

  const mediaLoader = useMediaLoader();

  const categoryTrialContent = useCategoryTrialContent();

  const categoryGetParams = useCategoryGetParamsStrategy(pageContextType);

  const { currentContent, section } = categoryGetParams;

  const contentMemberId = computed(() => {
    return currentContent?.value?.memberId || context.memberId;
  });

  const { contentUserTypeFromRoute } = useContentRouteHelper();

  const contents = useContentsForUserType(contentUserTypeFromRoute.value);

  const isParentChildPage = computed(
    () => profile.isParent && contentMemberId.value !== context.memberId
  );

  const categoryManager = useCategory(categoryGetParams);

  const { category } = categoryManager;

  const categoryLockState = ref<CategoryLockState>();

  const ancestorsLockState = ref<LockStates>();

  const isExam = useCategoryAncestorsIsExam(ancestors);

  const customer = useCategoryAncestorsCustomer(ancestors);

  const categoryDisplayIcon = useCategoryAncestorsDisplayIcon(ancestors);

  const { chapter } = useCategoryAncestorsChapter(ancestors);

  const mPageMedias: Ref<Map<string, MediaSchema>> = ref<
    Map<string, MediaSchema>
  >(new Map());

  const categoryMainMedia = useCategoryMainMedia(categoryDisplayIcon);

  watch(
    () => downloadedContents.value,
    (dContents) => {
      if (currentContent?.value?.id == null) return;
      const dContent = dContents.find(
        (content) => content.id === currentContent?.value?.id
      );

      if (!dContent) {
        const unDownLoadedContent = allContents.value.find(
          (content) => content.id === currentContent?.value?.id
        );
        contents.saveSlug('');
        navigateTo(unDownLoadedContent?.url || '/');
      }
    }
  );

  const init = () => {
    if (!currentContent.value) {
      // if libraryBookId is not in user contents
      if (mainContent.value) {
        // category missing
        throw new Eva2Error(
          ErrorTypes.LibraryBookMissing,
          { slug: route.params.lb_or_eval },
          `missing category with slug ${section.value}`
        );
      } else {
        // no mainContent the user has probably been logout
        window.location.href = '/logout';
        return;
      }
    }
    switch (section.value) {
      case Sections.ParentForYou:
      case Sections.Revisions:
        contents.saveSlug(currentContent.value.slug);
        break;
    }
  };

  const load = async () => {
    const loadingIndicator = useLoadingIndicator();
    loadingIndicator.start();
    await categoryGetParams.load();
    await categoryManager.load();
    await categoryTrialContent.load();
    mediaLoader.addIds(categoryManager.mediaIds);

    ancestors.value = await getCategoryAncestors(
      $db,
      category.value,
      currentContent.value.id
    );

    categoryLockState.value = getCategoryLockStates(
      profile.isASubscriber,
      currentContent.value,
      ancestors.value,
      profile.lockedContent
    );

    ancestorsLockState.value = categoryLockState.value.ancestorsLockState;

    const { customerCategory } = customer;
    if (customerCategory.value && customerCategory.value.media)
      mediaLoader.addId(customerCategory.value.media);
    if (customerCategory.value && customerCategory.value.customerMedia)
      mediaLoader.addId(customerCategory.value.customerMedia);

    if (toValue(categoryDisplayIcon.mediaIcon)) {
      mediaLoader.addId(toValue(categoryDisplayIcon.mediaIcon));
    }

    mPageMedias.value = await mediaLoader.retrieveAll();

    await customer.load();

    categoryMainMedia.attachMapMedia(mPageMedias.value);

    loadingIndicator.finish();
    loadingIndicator.clear();
  };

  const discipline = computed(() => {
    if (category.value.contentType === 'discipline') return category.value;
    return toValue(useCategoryAncestorsDiscipline(ancestors));
  });

  const disciplineId = computed(() => {
    return (
      category.value.disciplineId ||
      toValue(useCategoryAncestorsDiscipline(ancestors).id)
    );
  });

  const release = () => {
    // unwatchRedirect();
    if (categoryManager) categoryManager.unload();
  };

  return {
    categoryManager,
    categoryLockState,
    categoryMainMedia,
    libraryBookId: currentContent?.value?.id,
    ancestorsLockState,
    ancestors,
    isExam,
    customer,
    mPageMedias,
    disciplineId,
    discipline,
    chapter,
    pageContextType,
    pageContentType,
    section,
    isParentChildPage,
    contents,
    contentMemberId,
    currentContent,
    categoryTrialContent,
    init,
    load,
    release,
  };
};
